/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.menu = document.querySelector('.main-menu__content');
      this.dropdowns = this.querySelectorAll('.announcement__nav-item > details');
      this.mobileNav = this.querySelector('.announcement__mobile-nav');
      this.mobileNavItems = [];
      // isPaused is the transient hover/focus pause; isManuallyPaused is the
      // sticky pause the visible toggle sets. They are kept separate so that
      // moving the mouse away (resumeSlider) never clears an explicit pause.
      this.isPaused = false;
      this.isManuallyPaused = false;
      this.toggleBtn = this.querySelector('.announcement__carousel-toggle');
      this.closeTimeout = null;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.moveNavHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveNavHandler);
      }
    }

    bindEvents() {
      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.mobileNav) {
        this.moveNavHandler = this.moveNavHandler
          || debounce(this.moveNavToMobile.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveNavHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
        this.slider.addEventListener('focusin', this.pauseSlider.bind(this));
        this.slider.addEventListener('focusout', this.resumeSlider.bind(this));
      }

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', this.toggleManualPause.bind(this));
      }

      // Desktop dropdown hover and keyboard behavior
      if (this.dropdowns.length) {
        this.dropdowns.forEach((details) => {
          const summary = details.querySelector('summary');

          details.addEventListener('mouseenter', () => {
            clearTimeout(this.closeTimeout);
            this.dropdowns.forEach((other) => {
              if (other !== details) {
                other.removeAttribute('open');
                other.querySelector('summary')?.setAttribute('aria-expanded', 'false');
              }
            });
            details.setAttribute('open', '');
            summary?.setAttribute('aria-expanded', 'true');
          });

          details.addEventListener('mouseleave', () => {
            this.closeTimeout = setTimeout(() => {
              details.removeAttribute('open');
              summary?.setAttribute('aria-expanded', 'false');
            }, 200);
          });

          // Keep aria-expanded in sync for keyboard users (toggle via Enter/Space)
          summary?.addEventListener('click', () => {
            // details.open reflects state BEFORE the toggle, so invert
            summary.setAttribute('aria-expanded', details.open ? 'false' : 'true');
          });

          // Close on Escape and return focus to trigger
          details.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && details.open) {
              details.removeAttribute('open');
              summary?.setAttribute('aria-expanded', 'false');
              summary?.focus();
            }
          });
        });
      }
    }

    /**
     * Initialise sliders and move elements into mobile nav
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (prefersReducedMotion.matches) {
          return;
        }

        const nextSlide = () => {
          if (prefersReducedMotion.matches) {
            return;
          }
          if (!this.isPaused && !this.isManuallyPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            // Index within the actual slides, not parentNode.children: the
            // carousel group also contains the pause button, so children would
            // be off by one. `slides` is the .announcement__text NodeList.
            const currSlideIndex = Array.from(slides).indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            if (document.dir === 'rtl') {
              this.slider.scrollTo(
                -Math.abs(nextSlideIndex * slides[nextSlideIndex].clientWidth),
                0
              );
            } else {
              this.slider.scrollTo(slides[nextSlideIndex].offsetLeft - 32, 0);
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.localization) {
        this.moveLocalization();
      }

      this.moveNavToMobile();
    }

    /**
     * Move announcement nav links into/out of the mobile hamburger menu
     * based on the current breakpoint. On mobile, items appear as native
     * main-nav entries with the same slide-right behavior.
     */
    moveNavToMobile() {
      if (!this.mobileNav) return;

      const mainNavList = document.querySelector('.main-menu__content .main-nav');
      if (!mainNavList) return;

      if (!theme.mediaMatches.md && this.mobileNavItems.length === 0) {
        // Mobile: move items into the main nav
        const items = this.mobileNav.querySelectorAll(':scope > ul > li');
        items.forEach((li) => {
          this.mobileNavItems.push(li);
          mainNavList.appendChild(li);
        });
      } else if (theme.mediaMatches.md && this.mobileNavItems.length > 0) {
        // Desktop: move items back into the hidden container
        const holder = this.mobileNav.querySelector('ul');
        this.mobileNavItems.forEach((li) => holder.appendChild(li));
        this.mobileNavItems = [];
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }

    /**
     * Toggles the sticky manual pause from the visible control, and swaps the
     * button's accessible name (Pause <-> Play). The advance loop re-arms every
     * tick regardless of state, so flipping the flag is all that is needed;
     * no timer teardown or restart.
     */
    toggleManualPause() {
      this.isManuallyPaused = !this.isManuallyPaused;
      this.toggleBtn.classList.toggle('is-paused', this.isManuallyPaused);
      const labelEl = this.toggleBtn.querySelector('.js-carousel-toggle-label');
      if (labelEl) {
        labelEl.textContent = this.isManuallyPaused
          ? this.toggleBtn.dataset.labelPlay
          : this.toggleBtn.dataset.labelPause;
      }
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
